import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../api';
import {
  BookOpen,
  RefreshCw,
  Download,
  ArrowDownLeft,
  ArrowUpRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Ban,
  Printer,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import ChipBankTabs from '../components/ChipBankTabs';
import { eposPrintReceipt, printReceipt } from '../utils/receiptPrinter';

// ----------------------------------------------------------------
// Ledger Report
// ----------------------------------------------------------------
// Read-only report of every chip-wallet ledger entry for a date range
// (defaults to today). Admin-only. Backed by GET /admin/cage/ledger.
// The filtered view can be exported to CSV.

const fmtCents = (cents) => {
  if (cents == null) return '$0.00';
  return (Number(cents) / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });
};

const fmtDateTime = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const todayStr = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

// YYYY-MM-DD for N days before today (local/casino time).
const daysAgoStr = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

function csvEscape(value) {
  const s = String(value == null ? '' : value);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

// Sort value for an entry given a column key.
function sortValue(e, key) {
  switch (key) {
    case 'createdAt': return new Date(e.createdAt).getTime() || 0;
    case 'type': return (e.entryLabel || '').toLowerCase();
    case 'player': return (e.player?.name || '').toLowerCase();
    case 'cashier': return (e.cashier?.name || '').toLowerCase();
    // Signed amount: credits positive, debits negative.
    case 'amount': return (e.direction === 'credit' ? 1 : -1) * (e.amountCents || 0);
    case 'balance': return e.balanceAfterCents || 0;
    default: return 0;
  }
}

export default function LedgerReport() {
  // Default to the trailing 7 days (start = 6 days ago, end = today).
  const [startDate, setStartDate] = useState(() => daysAgoStr(6));
  const [endDate, setEndDate] = useState(todayStr);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', dir: 'desc' });
  const [voidModal, setVoidModal] = useState({ open: false, entry: null });
  const [voidForm, setVoidForm] = useState({ employeeNumber: '', staffPin: '', reason: '' });
  const [voidLoading, setVoidLoading] = useState(false);
  const [voidError, setVoidError] = useState('');
  const [receiptCfg, setReceiptCfg] = useState({ printerHost: '', copies: 2 });
  const [reprintingId, setReprintingId] = useState(null);

  // Load receipt-printing settings (printer host + copies) so ledger
  // reprints use the same direct-to-printer path as the cage.
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/admin/cage/receipt-printing/settings');
        setReceiptCfg({
          printerHost: res.data.printerHost || '',
          copies: res.data.copies || 2,
        });
      } catch (e) {
        /* silent — reprints fall back to the browser print dialog */
      }
    })();
  }, []);

  // Reprint the receipt for a ledger entry. Uses the ePOS direct-print
  // path when a printer host is configured, otherwise the browser dialog.
  const reprintReceipt = async (e) => {
    setReprintingId(e.id);
    try {
      const receipt = {
        type: e.direction === 'credit' ? 'deposit' : 'withdrawal',
        reference: e.id,
        dateTime: e.createdAt,
        playerName: e.player?.name || '',
        memberNumber: e.player?.memberNumber || null,
        amountCents: e.amountCents,
        balanceAfterCents: e.balanceAfterCents,
        cashierEmployeeNumber: e.cashier?.employeeNumber || null,
      };
      const host = (receiptCfg.printerHost || '').trim();
      const copies = receiptCfg.copies || 1;
      if (host) {
        const res = await eposPrintReceipt(receipt, copies, host, { label: 'REPRINT' });
        if (res?.ok) {
          toast.success('Receipt reprinted');
        } else {
          await printReceipt(receipt, copies);
          toast('Sent to browser print dialog');
        }
      } else {
        await printReceipt(receipt, copies);
      }
    } catch (err) {
      console.error('Reprint failed:', err);
      toast.error('Failed to reprint receipt');
    } finally {
      setReprintingId(null);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Day boundaries are computed in the browser's local timezone —
      // i.e. the cage's — so "today" means the casino's day.
      const start = new Date(`${startDate}T00:00:00`).toISOString();
      const end = new Date(`${endDate}T23:59:59.999`).toISOString();
      const res = await api.get('/admin/cage/ledger', {
        params: { start, end },
      });
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to load ledger');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    load();
  }, [load]);

  const exportCsv = () => {
    const rows = sortedEntries;
    if (!rows.length) {
      toast.error('Nothing to export for this range');
      return;
    }
    const header = [
      'Date/Time',
      'Type',
      'Player',
      'Club #',
      'Cashier',
      'Direction',
      'Amount (USD)',
      'Balance After (USD)',
      'Note',
    ];
    const lines = rows.map((e) => [
      new Date(e.createdAt).toLocaleString('en-US'),
      e.entryLabel,
      e.player?.name || '',
      e.player?.memberNumber || '',
      e.cashier?.name || '',
      e.direction === 'credit' ? 'Credit (in)' : 'Debit (out)',
      (e.amountCents / 100).toFixed(2),
      (e.balanceAfterCents / 100).toFixed(2),
      e.note || '',
    ]);
    const csv = [header, ...lines]
      .map((row) => row.map(csvEscape).join(','))
      .join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chip-ledger_${startDate}_to_${endDate}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const summary = data?.summary;
  const entries = data?.entries || [];

  const VOIDABLE_TYPES = new Set(['deposit', 'adjustment_credit', 'adjustment_debit']);
  const canVoid = (e) => VOIDABLE_TYPES.has(e.entryType) && !e.reversed && !e.isReversal;

  const submitVoid = async () => {
    if (!voidModal.entry || !voidForm.employeeNumber || !voidForm.staffPin || !voidForm.reason.trim()) return;
    setVoidLoading(true);
    setVoidError('');
    try {
      await api.post(`/admin/cage/ledger/${voidModal.entry.id}/void`, {
        reason: voidForm.reason.trim(),
        employeeNumber: voidForm.employeeNumber,
        staffPin: voidForm.staffPin,
      });
      toast.success('Transaction voided');
      setVoidModal({ open: false, entry: null });
      setVoidForm({ employeeNumber: '', staffPin: '', reason: '' });
      load();
    } catch (err) {
      setVoidError(err?.response?.data?.error || 'Failed to void transaction');
    } finally {
      setVoidLoading(false);
    }
  };

  const sortedEntries = useMemo(() => {
    if (!sortConfig.key) return entries;
    const dir = sortConfig.dir === 'asc' ? 1 : -1;
    return [...entries].sort((a, b) => {
      const av = sortValue(a, sortConfig.key);
      const bv = sortValue(b, sortConfig.key);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [entries, sortConfig]);

  const setSort = (key) =>
    setSortConfig((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: key === 'createdAt' ? 'desc' : 'asc' },
    );

  const sortIcon = (key) => {
    if (sortConfig.key !== key) return <ArrowUpDown size={12} className="text-gray-600" />;
    return sortConfig.dir === 'asc'
      ? <ArrowUp size={12} className="text-amber-500" />
      : <ArrowDown size={12} className="text-amber-500" />;
  };

  const SortTh = ({ k, children, align }) => (
    <th
      onClick={() => setSort(k)}
      className={`px-4 py-3 font-medium cursor-pointer select-none hover:text-gray-200 transition-colors ${align === 'right' ? 'text-right' : ''}`}
    >
      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'justify-end w-full' : ''}`}>
        {children} {sortIcon(k)}
      </span>
    </th>
  );

  return (
    <div className="p-6 space-y-6">
      <ChipBankTabs />

      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-white flex items-center gap-2">
            <BookOpen className="text-amber-500" size={26} />
            Player Bank — Ledger
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            Every chip-wallet transaction for the selected dates.
          </p>
        </div>
        <button
          onClick={exportCsv}
          disabled={!entries.length}
          className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-black font-medium rounded-lg disabled:opacity-50 flex items-center gap-2"
        >
          <Download size={16} /> Export CSV
        </button>
      </header>

      {/* Date filters */}
      <section className="bg-gray-800 border border-gray-700 rounded-xl p-5">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs uppercase tracking-wide text-gray-400 mb-1">
              From
            </label>
            <input
              type="date"
              value={startDate}
              max={endDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-amber-500"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wide text-gray-400 mb-1">
              To
            </label>
            <input
              type="date"
              value={endDate}
              min={startDate}
              max={todayStr()}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-amber-500"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => {
                setStartDate(todayStr());
                setEndDate(todayStr());
              }}
              className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg"
            >
              Today
            </button>
            <button
              onClick={load}
              className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg flex items-center gap-2"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </div>
      </section>

      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <SummaryCard label="Entries" value={summary.count} />
          <SummaryCard
            label="Total in"
            value={fmtCents(summary.totalCreditCents)}
            tone="emerald"
          />
          <SummaryCard
            label="Total out"
            value={fmtCents(summary.totalDebitCents)}
            tone="red"
          />
          <SummaryCard
            label="Net change"
            value={fmtCents(summary.netCents)}
            tone="amber"
          />
        </div>
      )}

      {summary?.truncated && (
        <div className="bg-amber-900/30 border border-amber-700/50 rounded-lg px-4 py-2 text-amber-200 text-sm">
          Showing the first {entries.length.toLocaleString()} entries — narrow
          the date range to see the rest.
        </div>
      )}

      {/* Table */}
      <section className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-gray-400 flex items-center gap-3">
            <RefreshCw className="animate-spin" size={18} /> Loading ledger...
          </div>
        ) : entries.length === 0 ? (
          <div className="p-8 text-gray-500 text-sm">
            No ledger entries in this date range.
          </div>
        ) : (
          <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-900 sticky top-0">
                <tr className="text-left text-gray-400">
                  <SortTh k="createdAt">Date / Time</SortTh>
                  <SortTh k="type">Type</SortTh>
                  <SortTh k="player">Player</SortTh>
                  <SortTh k="cashier">Cashier</SortTh>
                  <SortTh k="amount" align="right">Amount</SortTh>
                  <SortTh k="balance" align="right">Balance after</SortTh>
                  <th className="px-4 py-3 font-medium text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {sortedEntries.map((e) => (
                  <tr
                    key={e.id}
                    className="border-t border-gray-700/60 hover:bg-gray-700/20"
                  >
                    <td className="px-4 py-2.5 text-gray-300 whitespace-nowrap">
                      {fmtDateTime(e.createdAt)}
                    </td>
                    <td className="px-4 py-2.5 text-white">
                      {e.entryLabel}
                      {e.note && (
                        <span className="block text-xs text-gray-500">
                          {e.note}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-gray-300">
                      {e.player?.name || '—'}
                      {e.player?.memberNumber && (
                        <span className="text-gray-500">
                          {' '}
                          · #{e.player.memberNumber}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-gray-400">
                      {e.cashier?.name || '—'}
                    </td>
                    <td
                      className={`px-4 py-2.5 text-right font-mono whitespace-nowrap ${
                        e.direction === 'credit'
                          ? 'text-emerald-400'
                          : 'text-red-400'
                      }`}
                    >
                      <span className="inline-flex items-center gap-1 justify-end">
                        {e.direction === 'credit' ? (
                          <ArrowDownLeft size={13} />
                        ) : (
                          <ArrowUpRight size={13} />
                        )}
                        {e.direction === 'credit' ? '+' : '−'}
                        {fmtCents(e.amountCents)}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-gray-300 whitespace-nowrap">
                      {fmtCents(e.balanceAfterCents)}
                    </td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      {!e.isReversal && (
                        <button
                          onClick={() => reprintReceipt(e)}
                          disabled={reprintingId === e.id}
                          className="mr-1.5 px-2.5 py-1 text-xs font-medium rounded-md bg-gray-600/30 text-gray-200 border border-gray-600/50 hover:bg-gray-600/50 transition-colors inline-flex items-center gap-1 disabled:opacity-50"
                          title="Reprint receipt"
                        >
                          {reprintingId === e.id ? <RefreshCw size={12} className="animate-spin" /> : <Printer size={12} />}
                          Reprint
                        </button>
                      )}
                      {e.reversed ? (
                        <span className="text-xs text-gray-500 italic">Voided</span>
                      ) : e.isReversal ? (
                        <span className="text-xs text-gray-600">Reversal</span>
                      ) : canVoid(e) ? (
                        <button
                          onClick={() => {
                            setVoidError('');
                            setVoidForm({ employeeNumber: '', staffPin: '', reason: '' });
                            setVoidModal({ open: true, entry: e });
                          }}
                          className="px-2.5 py-1 text-xs font-medium rounded-md bg-red-600/20 text-red-300 border border-red-600/40 hover:bg-red-600/30 transition-colors inline-flex items-center gap-1"
                        >
                          <Ban size={12} /> Void
                        </button>
                      ) : (
                        <span className="text-gray-700">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Void confirmation modal (supervisor PIN + reason) */}
      {voidModal.open && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setVoidModal({ open: false, entry: null })}>
          <div className="bg-gray-800 rounded-xl p-5 w-96 shadow-2xl border border-gray-700" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold flex items-center gap-2"><Ban size={18} className="text-red-400" /> Void transaction</h3>
              <button onClick={() => setVoidModal({ open: false, entry: null })} className="text-gray-400 hover:text-white"><X size={16} /></button>
            </div>
            <div className="bg-gray-700/50 rounded-lg p-3 mb-4 text-sm">
              <div className="text-gray-400 text-xs">Posts an append-only reversing entry. This cannot be undone.</div>
              <div className="text-white mt-1">
                {voidModal.entry?.entryLabel} · {voidModal.entry && (voidModal.entry.direction === 'credit' ? '+' : '−')}{voidModal.entry && fmtCents(voidModal.entry.amountCents)}
              </div>
              {voidModal.entry?.player?.name && <div className="text-gray-400 text-xs mt-0.5">{voidModal.entry.player.name}</div>}
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-400 block mb-1">Reason</label>
                <input type="text" value={voidForm.reason} onChange={(e) => setVoidForm((f) => ({ ...f, reason: e.target.value }))} placeholder="e.g. wrong amount entered" className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500" autoFocus />
              </div>
              <div>
                <label className="text-xs text-gray-400 block mb-1">Supervisor Employee #</label>
                <input type="text" value={voidForm.employeeNumber} onChange={(e) => setVoidForm((f) => ({ ...f, employeeNumber: e.target.value }))} placeholder="Employee number" className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500" />
              </div>
              <div>
                <label className="text-xs text-gray-400 block mb-1">Supervisor PIN</label>
                <input type="password" value={voidForm.staffPin} onChange={(e) => setVoidForm((f) => ({ ...f, staffPin: e.target.value }))} onKeyDown={(e) => e.key === 'Enter' && submitVoid()} placeholder="PIN" className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500" />
              </div>
            </div>
            {voidError && <div className="mt-3 p-2 bg-red-500/20 border border-red-500/30 rounded-lg text-xs text-red-400">{voidError}</div>}
            <div className="flex gap-2 mt-4">
              <button onClick={() => setVoidModal({ open: false, entry: null })} className="flex-1 px-3 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 text-sm rounded-lg">Cancel</button>
              <button onClick={submitVoid} disabled={voidLoading || !voidForm.employeeNumber || !voidForm.staffPin || !voidForm.reason.trim()} className="flex-1 px-3 py-2 bg-red-600 hover:bg-red-500 disabled:bg-gray-600 disabled:text-gray-400 text-white text-sm font-medium rounded-lg flex items-center justify-center gap-2">
                {voidLoading ? (<><RefreshCw size={14} className="animate-spin" /> Voiding...</>) : 'Void transaction'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, tone }) {
  const toneClass =
    tone === 'emerald'
      ? 'text-emerald-400'
      : tone === 'red'
        ? 'text-red-400'
        : tone === 'amber'
          ? 'text-amber-400'
          : 'text-white';
  return (
    <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
      <div className="text-xs uppercase tracking-wide text-gray-400">
        {label}
      </div>
      <div className={`text-2xl font-semibold mt-1 ${toneClass}`}>{value}</div>
    </div>
  );
}
